import { Button } from '@mantine/core';
import { useNavigate } from 'react-router';
import { useClient } from 'src/clients/hooks/useClient';
import { useEmailDocument } from 'src/common/hooks/useEmailDocument';
import EmailDocumentButton from 'src/components/convenience/buttons/EmailDocumentButton';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import type { EmailDocumentType } from 'src/meeting-note/hooks/useGenerateEmailContent';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

// The standard edit/download/email action set for a generated document, shared so a notification
// card renders the same buttons as the document row. Each button appears only when its input is
// present: editPath when editing is allowed, fileId when the file exists, email when the backend
// supports emailing that document type.
export default function DocumentActions({
  clientId,
  documentId,
  fileId,
  editPath,
  email,
  onClose,
}: {
  clientId: string;
  documentId: string;
  fileId: number | null;
  editPath?: string;
  email?: { type: EmailDocumentType; label: string };
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { client } = useClient(clientId);
  const clientName = client
    ? formatName(client.firstName, client.lastName, NameFormat.NATURAL)
    : undefined;
  const { createLink, isLoading: isDownloading } = useFileTemporaryLinkMutation();
  const { handleEmailClick, handleDeleteEmail, isGenerating, isDeleting } = useEmailDocument();

  const download = async () => {
    if (fileId == null) return;
    try {
      const link = await createLink(fileId);
      window.open(link.url, '_blank');
    } catch (e) {
      notifyAndLogError(
        'Failed to download document',
        'An error occurred while downloading the document. Please try again later.',
        e,
        { clientId, fileId },
      );
    }
  };

  return (
    <>
      {editPath && (
        <Button
          size="sm"
          variant="default"
          onClick={event => {
            event.stopPropagation();
            navigate(editPath);
            onClose();
          }}
        >
          Edit
        </Button>
      )}
      {email && (
        <span onClick={event => event.stopPropagation()} style={{ display: 'inline-flex' }}>
          <EmailDocumentButton
            documentId={documentId}
            isGenerating={isGenerating}
            isDeleting={isDeleting}
            onEmailClick={id => handleEmailClick(id, email.type, { clientId })}
            onDelete={handleDeleteEmail}
            clientName={clientName}
            documentLabel={email.label}
            size="sm"
            width="auto"
            showIcon={false}
            label="Email"
          />
        </span>
      )}
      {fileId != null && (
        <Button
          size="sm"
          variant="default"
          loading={isDownloading}
          onClick={event => {
            event.stopPropagation();
            download();
          }}
        >
          Download
        </Button>
      )}
    </>
  );
}
